import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { isNil, omitBy, omit } from "lodash";
import errorHandler from "Utils/error-handler";
import moment from "moment";

//SHARED
import {
  AlertBox,
  DataTable,
  Button,
  BasicModal,
  SelectInput,
  ActionMenu,
} from "@life-line-lab/components-react";

import { OrderNotesSchema } from "./order-notes-validation";

//STYLE
import "./order-notes.scss";
import { Space, Row, Col, Radio, Divider, Timeline } from "antd";
import { Input } from "antd";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { MoreOutlined } from "@ant-design/icons";
import useAccessControlList from "Hooks/access-control.hook";
import { useSession } from "Hooks/index";

interface DataType {
  key: React.Key;
  date: string;
  noteType: string;
  note: string;
  noteBy: string;
  status: string;
  resolvedDate: string;
}

const DeliveryUpdates = ({ onSubmit, onUpdate, hideModal, orderDetails }) => {
  const { t } = useTranslation();
  const { roleTypes } = useSession();

  const initialInput = {
    noteType: "",
    note: "",
    noteStatus: "",
  };

  const initialError = {};
  const [type, setType] = useState("idle");
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Order_Management",
  });

  const showUpdateOption = permission.rights.includes("UPDATE");
  const initialModal: any = {
    open: false,
  };
  const [modal, setModal] = useState<any>(initialModal);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleActionClick = (data?) => {
    setModal({ open: true, data: data });
    if (data) {
      setInput(data);
    } else {
      setInput(initialInput);
    }
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });
    setInput({ ...input, [name]: value });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
        resolvedAt: input.noteStatus == "OPEN" ? null : input.resolvedAt,
      };
      const value: any = OrderNotesSchema.validateSync(omitBy(valInput, isNil), {
        strict: false,
        abortEarly: true,
        stripUnknown: false,
      });
      type == "edit" ? onUpdate(value) : onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
    }
  };

  useEffect(() => {
    handleResetModalState();
  }, [hideModal]);

  const baseColumns: any = [
    {
      title: t("label.date").toLocaleUpperCase(),
      dataIndex: "createdAt",
      width: 80,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{moment(record?.createdAt).format("DD-MM-YY")}</span>;
      },
    },
    {
      title: t("label.noteType").toLocaleUpperCase(),
      dataIndex: "noteType",
      width: 60,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record.noteType}</span>;
      },
    },
    {
      title: t("label.note").toLocaleUpperCase(),
      dataIndex: "note",
      width: 120,
      render: (_: any, record: any) => {
        return <div className="note">{record.note}</div>;
      },
    },
    {
      title: t("label.noteBy").toLocaleUpperCase(),
      dataIndex: "createdBy",
      width: 75,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record.createdByName}</span>;
      },
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "noteStatus",
      width: 55,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span className="status">{record.noteStatus}</span>;
      },
    },
    {
      title: t("label.resolvedDate").toLocaleUpperCase(),
      dataIndex: "resolvedDate",
      width: 70,
      ellipsis: true,
      render: (_: any, record: any) => {
        return (
          <span>{record.resolvedAt ? moment(record?.resolvedAt).format("DD-MM-YY") : ""}</span>
        );
      },
    },
    {
      title: "",
      dataIndex: [],
      width: 20,
      ellipsis: true,
      render: (_: any, record: any, rowIndex: string) => (
        <ActionMenu
          label={<MoreOutlined />}
          actions={[
            {
              label: t("action.edit"),
              onClick: () => {
                setType("edit");
                handleActionClick(record);
              },
              isDisable: false,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="order-notes-div">
      <div className="order-notes">
        <Row className="p-30 mb-40 details-container">
          <DataTable
            columns={baseColumns}
            scroll={{ x: true }}
            dataSource={orderDetails?.notes || []}
            title={() => (
              <div className="notes-title">
                <label className="title">{t("label.notes")}</label>
                {showUpdateOption ? (
                  <Button
                    type="primary"
                    label={t("action.addNote")}
                    onClick={() => {
                      setType("create");
                      handleActionClick();
                    }}
                    icon={<PlusCircleOutlined />}
                  />
                ) : null}
              </div>
            )}
          />
        </Row>
      </div>
      <div>
        <BasicModal
          className="modal"
          isOpen={modal.open}
          isClosable={true}
          isMaskClosable={false}
          onClose={() => {
            setError(initialError);
            handleResetModalState();
          }}
        >
          <div className="modal-content-title">{t("label.createNote").toLocaleUpperCase()}</div>
          <div className="modal-content-text">
            <span className="bold">{t("label.order").toLocaleUpperCase()}</span>
            {orderDetails.orderCode}
            <br />
          </div>
          <div className="modal-content-text">
            <SelectInput
              showSearch
              options={[
                {
                  label: "Internal",
                  value: "Internal",
                },
                {
                  label: "Customer Support",
                  value: "Customer Support",
                },
              ]}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              name="noteType"
              placeholder={t("label.noteType")}
              label={t("label.noteType")}
              isRequired={true}
              value={input.noteType}
              onChange={(value) => handleSelectChange(value, "noteType")}
            />
            <AlertBox text={error.noteType} />
            <br />
            <label className="inp-label inp-label--required">{t("label.note")}</label>
            <Input.TextArea
              id="note"
              size="middle"
              autoSize={{ minRows: 5, maxRows: 6 }}
              name="note"
              required={true}
              value={input.note}
              placeholder={t("label.enterHere")}
              onChange={handleChange}
            />
            <AlertBox text={error.note} />
            <br />
            <SelectInput
              showSearch
              options={[
                {
                  label: "Open",
                  value: "OPEN",
                },
                {
                  label: "Resolved",
                  value: "RESOLVED",
                },
              ]}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              name="noteStatus"
              placeholder={t("placeholder.specialConditions")}
              label={t("label.status")}
              isRequired={true}
              value={input.noteStatus}
              onChange={(value) => handleSelectChange(value, "noteStatus")}
            />
            <AlertBox text={error.noteStatus} />
          </div>

          <div className="mt-30 right modal-end-button">
            <Button
              type={"primary"}
              label={t("action.confirm")}
              onClick={() => {
                handleSubmit();
              }}
            />
            <Button
              className="cancel-btn"
              type={"default"}
              label={t("action.cancel")}
              onClick={() => {
                handleResetModalState();
              }}
            />
          </div>
        </BasicModal>
      </div>
    </div>
  );
};

export default DeliveryUpdates;
